/**
 * Display-size (text + spacing comfort) preference.
 *
 * A device-local, user-facing comfort control that scales the whole app
 * shell up or down. It mirrors the locale-preference shape in
 * `src/i18n/detect.ts`: a localStorage-backed value resolved on the client,
 * applied to the document root as a `data-display-size` attribute.
 *
 * The attribute drives a single CSS `zoom` factor on `<html>` (see
 * `globals.css`) rather than per-component font tokens. The app's type scale
 * is built from ~200 files of hard-coded `text-[Npx]` utilities, so a root
 * zoom is the one central lever that scales body text, helper text, metadata,
 * rows, cards, modals and the sidebar together — and `default` is a true
 * no-op (zoom 1), so existing layouts render byte-for-byte as before.
 *
 * Persistence is localStorage-only today, matching how the interface language
 * is stored (`fixcontrol.locale`). The read/write/apply seam is deliberately
 * small so a future server-side `user_preferences` column can back it without
 * touching the consumers.
 */

export const DISPLAY_SIZES = ["compact", "default", "comfortable", "large"] as const;
export type DisplaySize = (typeof DISPLAY_SIZES)[number];

export const DEFAULT_DISPLAY_SIZE: DisplaySize = "default";

/** Document-root attribute the CSS variants key off. */
export const DISPLAY_SIZE_ATTR = "data-display-size";

/** localStorage key — namespaced like `fixcontrol.locale`. */
export const DISPLAY_SIZE_STORAGE_KEY = "fixcontrol.displaySize";

export function isDisplaySize(value: unknown): value is DisplaySize {
  return typeof value === "string" && (DISPLAY_SIZES as readonly string[]).includes(value);
}

/** Coerce any untrusted value to a valid display size, falling back to the
 *  default. Use everywhere a value is read from storage / user input. */
export function coerceDisplaySize(value: unknown): DisplaySize {
  return isDisplaySize(value) ? value : DEFAULT_DISPLAY_SIZE;
}

export function readStoredDisplaySize(): DisplaySize | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(DISPLAY_SIZE_STORAGE_KEY);
    return isDisplaySize(v) ? v : null;
  } catch {
    return null;
  }
}

export function writeStoredDisplaySize(size: DisplaySize): void {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(DISPLAY_SIZE_STORAGE_KEY, size); } catch { /* ignore */ }
}

/** Resolve the size to render with on the client: stored preference, else
 *  the default. (No browser/URL source — comfort is an explicit choice.) */
export function resolveClientDisplaySize(): DisplaySize {
  return readStoredDisplaySize() ?? DEFAULT_DISPLAY_SIZE;
}

/** Write the attribute onto the document root so the CSS variants apply.
 *  Always normalises through `coerceDisplaySize` so a bad value can't leak
 *  an unknown attribute (and silently fall back to `default` zoom). */
export function applyDisplaySize(size: DisplaySize): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute(DISPLAY_SIZE_ATTR, coerceDisplaySize(size));
}

/**
 * Inline, render-blocking snippet for the document `<head>`/top-of-`<body>`.
 *
 * Sets the attribute from localStorage before first paint so a non-default
 * preference doesn't flash the default size on load. Kept dependency-free and
 * defensive (storage can throw in private mode) — mirrors the next-themes
 * no-flash pattern. The allow-list is inlined so the snippet stays
 * self-contained as a string.
 */
export function displaySizeNoFlashScript(): string {
  return `(function(){try{var k=${JSON.stringify(DISPLAY_SIZE_STORAGE_KEY)};var a=${JSON.stringify(DISPLAY_SIZE_ATTR)};var allowed=${JSON.stringify(DISPLAY_SIZES)};var v=window.localStorage.getItem(k);document.documentElement.setAttribute(a,allowed.indexOf(v)>=0?v:${JSON.stringify(DEFAULT_DISPLAY_SIZE)});}catch(e){}})();`;
}
